import type { ArabicNumerals } from '@syncanix/shared';

/**
 * Initial Arabic-numeral preference resolution + persistence for the dashboard
 * (ADR-0141 opt-in). Per the locked spec the default is **Western** (0-9);
 * a user opts Arabic into Eastern Arabic-Indic (٠-٩) and that choice persists.
 *
 * Mirrors `resolve-initial-locale` / `ThemeProvider` persistence convention
 * (`window.localStorage`, `syncanix:<thing>` key, SSR/test-safe `window`
 * guard). Pure of React — consumed by `Providers.tsx`, which passes the
 * resolved value as `initialNumerals` and `persistNumerals` as
 * `onNumeralsChange`. There is no browser-inferred default: Eastern numerals
 * are an explicit choice, so absent a saved preference we always start Western.
 */
export const NUMERALS_STORAGE_KEY = 'syncanix:numerals';

function isArabicNumerals(value: string): value is ArabicNumerals {
  return value === 'western' || value === 'eastern';
}

export function resolveInitialNumerals(): ArabicNumerals {
  if (typeof window === 'undefined') return 'western';
  const stored = window.localStorage.getItem(NUMERALS_STORAGE_KEY);
  return stored !== null && isArabicNumerals(stored) ? stored : 'western';
}

export function persistNumerals(numerals: ArabicNumerals): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(NUMERALS_STORAGE_KEY, numerals);
}
